// Geographic utilities using GeoJSON for precise nation detection
import europeanBorders from '../data/europeanBorders.json';

interface GeoJSONFeature {
  type: string;
  properties: {
    name: string;
    name_en?: string;
    admin?: string;
    iso_a3?: string;
  };
  geometry: {
    type: string;
    coordinates: number[][][] | number[][][][];
  };
}

interface GeoJSONFeatureCollection {
  type: string;
  features: GeoJSONFeature[];
}

/**
 * Ray-casting algorithm to determine if a point is inside a polygon
 * @param point [longitude, latitude]
 * @param polygon Array of [longitude, latitude] coordinates
 * @returns true if point is inside polygon
 */
function pointInPolygon(point: number[], polygon: number[][]): boolean {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    const intersect =
      (yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
}

/**
 * Check if a point is inside a MultiPolygon
 * @param point [longitude, latitude]
 * @param multiPolygon Array of polygons
 * @returns true if point is inside any of the polygons
 */
function pointInMultiPolygon(
  point: number[],
  multiPolygon: number[][][][]
): boolean {
  for (const polygon of multiPolygon) {
    // Each polygon can have multiple rings (exterior + holes)
    // Check if point is in exterior ring (first ring)
    if (polygon.length > 0 && pointInPolygon(point, polygon[0])) {
      // Check if point is NOT in any hole (subsequent rings)
      let inHole = false;
      for (let i = 1; i < polygon.length; i++) {
        if (pointInPolygon(point, polygon[i])) {
          inHole = true;
          break;
        }
      }
      if (!inHole) return true;
    }
  }
  return false;
}

/**
 * Get nation name from coordinates using GeoJSON point-in-polygon detection
 * @param latitude
 * @param longitude
 * @returns Nation name or empty string if not found
 */
export function getNationFromCoordinates(
  latitude: number,
  longitude: number
): string {
  const point = [longitude, latitude];
  const geoData = europeanBorders as GeoJSONFeatureCollection;

  for (const feature of geoData.features) {
    const { geometry, properties } = feature;

    if (geometry.type === 'Polygon') {
      const coordinates = geometry.coordinates as number[][][];
      // Check if point is in exterior ring (first ring)
      if (coordinates.length > 0 && pointInPolygon(point, coordinates[0])) {
        // Check if point is NOT in any hole (subsequent rings)
        let inHole = false;
        for (let i = 1; i < coordinates.length; i++) {
          if (pointInPolygon(point, coordinates[i])) {
            inHole = true;
            break;
          }
        }
        if (!inHole) {
          return properties.name_en || properties.name || properties.admin || '';
        }
      }
    } else if (geometry.type === 'MultiPolygon') {
      const coordinates = geometry.coordinates as number[][][][];
      if (pointInMultiPolygon(point, coordinates)) {
        return properties.name_en || properties.name || properties.admin || '';
      }
    }
  }

  return ''; // Not found
}

/**
 * Get nation code from coordinates
 * @param latitude
 * @param longitude
 * @returns Nation code (ISO A3) or empty string if not found
 */
export function getNationCodeFromCoordinates(
  latitude: number,
  longitude: number
): string {
  const point = [longitude, latitude];
  const geoData = europeanBorders as GeoJSONFeatureCollection;

  for (const feature of geoData.features) {
    const { geometry, properties } = feature;

    if (geometry.type === 'Polygon') {
      const coordinates = geometry.coordinates as number[][][];
      // Check if point is in exterior ring (first ring)
      if (coordinates.length > 0 && pointInPolygon(point, coordinates[0])) {
        // Check if point is NOT in any hole (subsequent rings)
        let inHole = false;
        for (let i = 1; i < coordinates.length; i++) {
          if (pointInPolygon(point, coordinates[i])) {
            inHole = true;
            break;
          }
        }
        if (!inHole) {
          return properties.iso_a3 || '';
        }
      }
    } else if (geometry.type === 'MultiPolygon') {
      const coordinates = geometry.coordinates as number[][][][];
      if (pointInMultiPolygon(point, coordinates)) {
        return properties.iso_a3 || '';
      }
    }
  }

  return '';
}
